import { THEME_PROTOCOL, THEME_PROTOCOL_VERSION } from '../core/tokens';
import { isHttpOrigin, isThemeMessage } from '../core/validate';
import type { IResolvedTheme, IThemeMessage, ThemeMessageType } from '../types';

interface IThemeTrustOptions {
  trustedOrigins: readonly string[];
  /** 仅本地 Vite 启动时开启；source、协议、session 校验不变 */
  allowAnyOrigin?: boolean;
  debug?: boolean;
}

export interface IThemeClientOptions extends IThemeTrustOptions {
  /** payload 未经信任，由接收方逐 token 归一化 */
  apply: (payload: unknown) => void;
  reset: () => void;
}

export interface IThemeHostOptions extends IThemeTrustOptions {
  getWindow: () => Window | null | undefined;
  getOrigin: () => string;
  getTheme: () => IResolvedTheme;
}

const createMessage = (type: ThemeMessageType, sessionId: string, requestId: number): IThemeMessage => ({
  protocol: THEME_PROTOCOL,
  version: THEME_PROTOCOL_VERSION,
  type,
  sessionId,
  requestId
});

const createDebug = (enabled: boolean | undefined, scope: string) => (reason: string) => {
  if (enabled) console.debug(`[theme:${scope}]`, reason);
};

/** 被嵌侧：session 属于子文档；ACK 仅通知，不参与内容加载或重试状态机 */
export function createThemeClient(options: IThemeClientOptions): () => void {
  const debug = createDebug(options.debug, 'client');
  if (window.parent === window || (!options.allowAnyOrigin && !options.trustedOrigins.length)) return () => {};
  const sessionId = `theme-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  let lastRequestId = -1;
  let parentOrigin = '';
  const handleMessage = (event: MessageEvent) => {
    if (event.data?.protocol !== THEME_PROTOCOL) return;
    if (event.source !== window.parent) return debug('rejected: source');
    const isTrusted = options.allowAnyOrigin ? isHttpOrigin(event.origin) : options.trustedOrigins.includes(event.origin);
    if (!isTrusted) return debug('rejected: origin');
    if (!isThemeMessage(event.data)) return debug('rejected: protocol/payload');
    const message = event.data;
    if (message.sessionId !== sessionId || (parentOrigin && event.origin !== parentOrigin)) return debug('rejected: session');
    if (!['APPLY', 'RESET'].includes(message.type) || message.requestId < lastRequestId) {
      return debug('rejected: type/stale request');
    }
    parentOrigin = event.origin;
    if (message.requestId > lastRequestId) {
      if (message.type === 'APPLY') options.apply(message.payload);
      else options.reset();
      lastRequestId = message.requestId;
      debug(message.type);
    }
    window.parent.postMessage(createMessage('APPLIED', sessionId, message.requestId), event.origin);
  };
  const handleReady = () => {
    // READY 不含主题，允许未知父 origin；APPLY / ACK 仍使用精确 origin
    const origins = options.allowAnyOrigin ? ['*'] : options.trustedOrigins;
    for (const origin of origins) window.parent.postMessage(createMessage('READY', sessionId, 0), origin);
    debug('READY');
  };
  window.addEventListener('message', handleMessage);
  window.addEventListener('load', handleReady);
  handleReady();
  return () => {
    window.removeEventListener('message', handleMessage);
    window.removeEventListener('load', handleReady);
    options.reset();
  };
}

/** 宿主侧：每个 iframe 一个实例；READY 建立 session 后发送完整快照 */
export function createThemeHost(options: IThemeHostOptions) {
  const debug = createDebug(options.debug, 'host');
  let sessionId = '';
  let requestId = 0;
  const retiredSessions = new Set<string>();
  const isTrusted = () =>
    options.allowAnyOrigin ? isHttpOrigin(options.getOrigin()) : options.trustedOrigins.includes(options.getOrigin());
  const send = (type: 'APPLY' | 'RESET') => {
    if (!isTrusted()) return debug('skipped: target origin');
    if (!sessionId) return debug('waiting: READY');
    const target = options.getWindow();
    if (!target) return debug('skipped: missing iframe window');
    const message = createMessage(type, sessionId, ++requestId);
    if (type === 'APPLY') message.payload = options.getTheme();
    target.postMessage(message, options.getOrigin());
    debug(type);
  };
  const handleMessage = (event: MessageEvent) => {
    if (event.data?.protocol !== THEME_PROTOCOL) return;
    if (event.source !== options.getWindow()) return debug('rejected: source');
    if (!isTrusted() || event.origin !== options.getOrigin()) return debug('rejected: origin');
    if (!isThemeMessage(event.data)) return debug('rejected: protocol/payload');
    const message = event.data;
    if (message.type === 'READY') {
      if (retiredSessions.has(message.sessionId)) return debug('rejected: retired session');
      if (sessionId !== message.sessionId) {
        if (sessionId) retiredSessions.add(sessionId);
        sessionId = message.sessionId;
        requestId = 0;
      }
      send('APPLY');
    } else if (message.type === 'APPLIED' && message.sessionId === sessionId && message.requestId === requestId) {
      debug('APPLIED');
    }
  };
  const resetSession = () => {
    if (sessionId) retiredSessions.add(sessionId);
    sessionId = '';
    requestId = 0;
  };
  window.addEventListener('message', handleMessage);
  debug('listening');
  return {
    apply: () => send('APPLY'),
    reset: () => send('RESET'),
    resetSession,
    dispose: () => {
      resetSession();
      window.removeEventListener('message', handleMessage);
    }
  };
}
